import type { TFunction } from "i18next";
import { DateTime } from "luxon";

import type {
  AnalyticsInterval,
  CertificationDetail,
  CertificationStats,
  ItemStats,
  ModulesAnalytics,
  ProgressionAnalytics,
  TracksAnalytics,
} from "@/redux/certifications/types";
import { getContentKey, getPathCopy } from "@/domains/certifications/utils";
import {
  ANALYTICS_DEFAULT_WEEKS,
  BUCKET_LABEL_FORMATS,
  FUNNEL_BAR_MAX_HEIGHT,
  FUNNEL_BAR_MIN_HEIGHT,
  FUNNEL_STAGES,
  INTERVALS,
  LOW_PASS_RATE,
  PASS_RATE_LEVELS,
  PROGRESSION_INTERVALS,
  PROGRESSION_RAMP,
} from "@/features/certifications/analytics/constants";
import type {
  AnalyticsFilterState,
  FunnelBar,
  ModuleContentRow,
  PassRateLevel,
  ProgressionSeries,
  TrackComparisonRow,
  TrackShare,
  TrackSwitchRow,
} from "@/features/certifications/analytics/types";

export const getFunnelBars = ({
  activeEnrollments,
  modules,
}: ModulesAnalytics): FunnelBar[] => [
  {
    key: FUNNEL_STAGES.ENROLLED,
    stage: FUNNEL_STAGES.ENROLLED,
    week: null,
    count: activeEnrollments,
    rate: null,
  },
  ...modules.map(
    (module, index): FunnelBar => ({
      key: `week-${module.id}`,
      stage:
        index === modules.length - 1
          ? FUNNEL_STAGES.LAST_WEEK
          : FUNNEL_STAGES.WEEK,
      week: index + 1,
      count: module.reached,
      rate: module.reachedRate,
    }),
  ),
];

export const getFunnelBarHeight = (count: number, enrolled: number) =>
  enrolled > 0
    ? Math.max(
        FUNNEL_BAR_MIN_HEIGHT,
        Math.round((count / enrolled) * FUNNEL_BAR_MAX_HEIGHT),
      )
    : FUNNEL_BAR_MIN_HEIGHT;

export const getPassRateLevel = (rate: number): PassRateLevel =>
  PASS_RATE_LEVELS.find((level) => rate >= level.min) ?? LOW_PASS_RATE;

export const toPercent = (rate: number) => Math.round(rate * 100);

export const getTrackShares = ({ tracks }: TracksAnalytics): TrackShare[] => {
  const total = tracks.reduce((sum, track) => sum + track.active, 0);
  return tracks.map((track) => ({
    id: track.id,
    slug: track.slug,
    title: track.title,
    count: track.active,
    percent: total > 0 ? (track.active / total) * 100 : 0,
  }));
};

export const countSwitches = ({ switches }: TracksAnalytics) =>
  switches.reduce((sum, row) => sum + row.switches, 0);

/** Hides stale data while a query refetches for new filters. */
export const getCardState = <T>(query: {
  data?: T;
  isFetching: boolean;
  isError: boolean;
}) => ({
  data: query.isFetching ? undefined : query.data,
  isLoading: query.isFetching,
  isError: !query.isFetching && query.isError,
});

const LUXON_UNITS = {
  DAY: "day",
  WEEK: FUNNEL_STAGES.WEEK,
  MONTH: "month",
} as const satisfies Record<AnalyticsInterval, string>;

/** Counts the points the BE would return, snapping to whole weeks and months as it does. */
export const countBuckets = (
  { startDate, endDate, interval }: AnalyticsFilterState,
  today: DateTime = DateTime.now(),
) => {
  const unit = LUXON_UNITS[interval];
  const end = endDate ? DateTime.fromISO(endDate) : today;
  const start = startDate
    ? DateTime.fromISO(startDate)
    : end.minus({ weeks: ANALYTICS_DEFAULT_WEEKS });
  return (
    Math.floor(
      end.startOf(unit).diff(start.startOf(unit), `${unit}s`).as(`${unit}s`),
    ) + 1
  );
};

export const getProgressionInterval = (
  interval: AnalyticsInterval,
): AnalyticsInterval =>
  PROGRESSION_INTERVALS.includes(interval) ? interval : INTERVALS.WEEK;

export const getBucketLabels = (
  points: { date: string }[],
  interval: AnalyticsInterval,
) => {
  const years = new Set(points.map((point) => point.date.slice(0, 4)));
  const format =
    years.size > 1 && interval !== INTERVALS.MONTH
      ? `${BUCKET_LABEL_FORMATS[interval]} yyyy`
      : BUCKET_LABEL_FORMATS[interval];
  return points.map((point) => DateTime.fromISO(point.date).toFormat(format));
};

export const getWeekByModule = ({ modules }: CertificationDetail) =>
  new Map(
    [...modules]
      .sort((a, b) => a.order - b.order)
      .map((module, index) => [module.id, index + 1]),
  );

export const getTrackLabel = (
  t: TFunction,
  certification: CertificationDetail,
  trackId: number,
  fallback: string,
) => {
  const track = certification.tracks.find((item) => item.id === trackId);
  return track
    ? getPathCopy(t, getContentKey(certification.type), track).shortTitle
    : fallback;
};

export const getRampColor = (index: number, count: number): string => {
  const last = PROGRESSION_RAMP.length - 1;
  const position = count <= 1 ? last : Math.round((index / (count - 1)) * last);
  return PROGRESSION_RAMP[Math.min(position, last)] ?? PROGRESSION_RAMP[0];
};

export const getProgressionSeries = ({
  modules,
  points,
}: ProgressionAnalytics): ProgressionSeries[] =>
  modules.map((module, index) => ({
    moduleId: module.id,
    week: index + 1,
    title: module.title,
    data: points.map(
      (point) =>
        point.modules.find((row) => row.module === module.id)?.learners ?? 0,
    ),
  }));

const getItemActivity = (t: TFunction, item: ItemStats) => {
  const prefix = "certification.admin.analytics.content.activity";
  if (item.video) {
    return t(`${prefix}.video`, {
      started: item.video.started,
      completed: item.video.completed,
      coverage:
        item.video.averageCoverage === null
          ? t("charts.noValue")
          : t("charts.percent", {
              percent: toPercent(item.video.averageCoverage),
            }),
    });
  }
  if (item.resource) {
    return t(`${prefix}.resource`, { count: item.resource.downloads });
  }
  if (item.knowledgeCheck) {
    const { attempted, passed, pendingReview } = item.knowledgeCheck;
    return t(`${prefix}.knowledgeCheck`, { attempted, passed, pendingReview });
  }
  if (item.community) {
    const { posts, comments, accepted, rejected, pending, contributors } =
      item.community;
    return t(`${prefix}.community`, {
      posts,
      comments,
      accepted,
      rejected,
      pending,
      contributors,
    });
  }
  return t(`${prefix}.none`);
};

export const getModuleContentRows = (
  t: TFunction,
  { modules }: ModulesAnalytics,
): ModuleContentRow[] => {
  const prefix = "certification.admin.analytics.content";
  return modules.map((module, index) => {
    const items = module.items.map((item) => ({
      id: item.id,
      key: `item-${item.id}`,
      title: item.title,
      subtitle: t(`${prefix}.types.${item.type}`),
      activity: getItemActivity(t, item),
    }));
    return {
      id: module.id,
      key: `module-${module.id}`,
      title: t(`${prefix}.week`, { position: index + 1, title: module.title }),
      subtitle: t(`${prefix}.items`, { count: items.length }),
      activity: t(`${prefix}.reached`, {
        count: module.reached,
        rate:
          module.reachedRate === null
            ? t("charts.noValue")
            : t("charts.percent", { percent: toPercent(module.reachedRate) }),
      }),
      ...(items.length > 0 ? { children: items } : {}),
    };
  });
};

export const getTrackComparisonRows = (
  { tracks }: TracksAnalytics,
  stats: CertificationStats | undefined,
  getLabel: (trackId: number, fallback: string) => string,
): TrackComparisonRow[] =>
  tracks.map((track) => {
    const summary = stats?.tracks.find((row) => row.id === track.id);
    return {
      id: track.id,
      label: getLabel(track.id, track.title),
      enrolled: summary?.enrolled ?? null,
      active: track.active,
      notStarted: track.notStarted,
      inProgress: track.inProgress,
      awaitingReview: track.awaitingReview,
      certified: track.certified,
      certificatesIssued: summary?.certificatesIssued ?? null,
      completionRate: track.completionRate,
      medianDaysToCertificate: track.medianDaysToCertificate,
    };
  });

export const getTrackSwitchRows = (
  { switches, tracks }: TracksAnalytics,
  getLabel: (trackId: number, fallback: string) => string,
): TrackSwitchRow[] => {
  const titleOf = (id: number) =>
    getLabel(id, tracks.find((track) => track.id === id)?.title ?? String(id));
  return switches.map((row) => ({
    key: `${row.fromTrack}-${row.toTrack}`,
    from: titleOf(row.fromTrack),
    to: titleOf(row.toTrack),
    switches: row.switches,
  }));
};
